import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOHeadProps {
  title: string;
  description: string;
  /** Optional JSON-LD structured data object */
  structuredData?: Record<string, unknown>;
  /** Override canonical path (defaults to current pathname) */
  canonicalPath?: string;
  /** Set to true to add noindex */
  noindex?: boolean;
}

const SITE_URL = 'https://twiceread.store';
const SITE_NAME = 'Twice Read';

const SEOHead = ({ title, description, structuredData, canonicalPath, noindex }: SEOHeadProps) => {
  const location = useLocation();
  const canonical = `${SITE_URL}${canonicalPath ?? location.pathname}`;

  useEffect(() => {
    // Title
    document.title = title;

    // Meta description
    const setMeta = (name: string, content: string, attr = 'name') => {
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (el) {
        el.setAttribute('content', content);
      } else {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        el.content = content;
        document.head.appendChild(el);
      }
    };

    setMeta('description', description);
    setMeta('og:title', title, 'property');
    setMeta('og:description', description, 'property');
    setMeta('twitter:title', title, 'name');
    setMeta('twitter:description', description, 'name');

    // Canonical link
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.rel = 'canonical';
      document.head.appendChild(link);
    }
    link.href = canonical;

    // Robots
    if (noindex) {
      setMeta('robots', 'noindex, nofollow');
    } else {
      const robotsMeta = document.querySelector('meta[name="robots"]');
      if (robotsMeta) robotsMeta.remove();
    }

    // Structured data
    const existingLd = document.querySelector('script[data-seo-ld]');
    if (existingLd) existingLd.remove();

    if (structuredData) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-ld', 'true');
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }

    return () => {
      // Cleanup structured data on unmount
      const ld = document.querySelector('script[data-seo-ld]');
      if (ld) ld.remove();
    };
  }, [title, description, canonical, noindex, structuredData]);

  return null;
};

export default SEOHead;
